import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DropletService } from '../../Services/Droplet.service';
import { InitProvider } from '../../init-provider';
import { LevelService } from '../../Services/level.service';
import { WebPageService } from '../../Services/webpage.service';

@Component({
  selector: 'app-home',
  templateUrl: './Home.component.html',
  styleUrls: ['./Home.component.scss']
})
export class HomeComponent implements OnInit {
  gridOverlay: any;
  serviceItems: any;
  testimonialData: any;
  counterContent: any;
  pricingContent: any;
  company: any;

  /** Educational levels from GET /public/levels, rendered as alternating text blocks */
  levels: any[] = [];

  /** Loading flags for skeleton placeholders */
  webPageLoading = true;
  levelsLoading = true;
  /** Number of skeleton level blocks shown while levels load */
  levelSkeletons = [0, 1];

  // ===== Content from GET /public/web-page =====
  /** Header "cover": rich-text title/subtitle, buttons and background image/video */
  cover: any = null;
  /** "Acerca de nosotros" — feeds the first text block */
  about: any = null;
  /** Controls the "Acerca de nosotros" full-description modal */
  aboutModalOpen = false;

  /** Contact form at the bottom, used by the "Comenzar ahora" CTA */
  @ViewChild('contactForm', { read: ElementRef }) contactForm?: ElementRef<HTMLElement>;

  /** Smooth-scroll down to the contact form */
  scrollToContact(): void {
    this.contactForm?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  /** "Propuesta de Valor" header */
  valuePropsTitle = '';
  valuePropsSubtitle = '';
  /** "Propuesta de Valor" cards mapped to the sliders-grid slide shape */
  valuePropsSlides: any[] = [];

  /** Modal showing a value proposition's full description */
  valuePropModalOpen = false;
  valuePropModalTitle = '';
  valuePropModalContent = '';

  /** Opens the modal with the clicked value proposition's full description */
  openValueProp(slide: any): void {
    this.valuePropModalTitle = slide?.title || '';
    this.valuePropModalContent = slide?.fullDescription || '';
    this.valuePropModalOpen = true;
  }

  /** Testimonials mapped to the testimonials block shape */
  testimonials: any[] = [];
  /** Testimonials section heading and eyebrow (rich-text from the API) */
  testimonialsTitle = '';
  testimonialsSubtitle = '';

  /** "Nuestra Casa" (our_house) items mapped to the gallery photo shape */
  galleryPhotos: { image: string; title: string; description?: string }[] = [];

  /** "Registrations" CTA block (title, button, image) from GET /public/web-page */
  registrations: any = null;

  /** Institutional data (lat/lng, address, phone, city, country) for the location map */
  general: any = null;

  /**
   * Portfolio client slider config
   */
  serviceItemSlideConfig = {
    'slidesToShow': 4,
    'slidesToScroll': 1,
    'cssEase': 'linear',
    'arrows': true,
    'dots': false,
    'autoplay': false,
    'autoplaySpeed': 1000,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1
        }
      }
    ]
  };

  /**
   * Recent Work Content Heading
   */
  recentWorkHeading: any = {
    title: 'Recent Work',
    content: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry\'s standard dummy text ever since the 1500s,'
  };

  /**
   * Explore Studio content
   */
  exploreStudio: any = ['assets/images/process/1.png', 'assets/images/process/2.png', 'assets/images/process/3.png'];

  /**
   * Client Says Content Heading
   */
  clientSaysHeading: any = {
    title: 'Client Says',
    content: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry\'s standard dummy text ever since the 1500s,'
  };

  /**
   * Content of Contact section
   */
  contactContent: any = {
    title: 'Become an ally of Reliable and Experienced Agency',
    content: 'Enter your details & we\'ll be in touch to discuss your project',
    email: 'support@theironnetwork.org',
    contactNumber: '+01 2345 678 910',
    contactTo: '+016272627273',
    image: 'https://via.placeholder.com/700x700/'
  };

  constructor(
    public service: DropletService,
    initProvider: InitProvider,
    private levelService: LevelService,
    private webPageService: WebPageService
  ) {
    this.company = initProvider.getSettings();
    console.log('este es el company', this.company);

  }

  async loadLevels(): Promise<void> {
    try {
      const res = await this.levelService.getLevels();
      // GET /public/levels returns { status, code, data: { data: [...] } }.
      // Be defensive in case the envelope is just { data: [...] }.
      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data)
          ? res.data
          : [];
      this.levels = list;
    } finally {
      this.levelsLoading = false;
    }
  }

  /** Fetch institutional web-page content (about + value propositions) */
  async loadWebPage(): Promise<void> {
    try {
      await this.fetchWebPage();
    } finally {
      this.webPageLoading = false;
    }
  }

  private async fetchWebPage(): Promise<void> {
    const res = await this.webPageService.getWebPage();
    const data = res?.data;
    if (!data) {
      return;
    }
    this.cover = data.cover || null;
    this.about = data.about || null;
    this.registrations = data.registrations || null;
    this.general = data.general || null;

    const vp = data.value_propositions || {};
    this.valuePropsTitle = vp.title || '';
    this.valuePropsSubtitle = vp.short_description || '';
    this.valuePropsSlides = Array.isArray(vp.items)
      ? vp.items.map(item => ({
          title: item.title || '',
          // Short text in the listing, full (rich-text) description in the modal.
          // Fall back to the long description in the listing while `short_description`
          // is being populated, so cards never render empty.
          description: item.short_description || item.description || '',
          fullDescription: item.description || '',
          image: item.image || '',
          url: '#',
          bgType: item.background_type || 'none',
          backgroundColor: item.background_value || undefined,
          backgroundColorHover: item.background_value || undefined,
          hasImage: !!item.image
        }))
      : [];

    // `testimonials` now comes as { title, subtitle (rich-text), items: [...] }
    const testimonialsBlock = data.testimonials || {};
    this.testimonialsTitle = testimonialsBlock.title || '';
    this.testimonialsSubtitle = testimonialsBlock.subtitle || '';
    const testimonialItems = Array.isArray(testimonialsBlock.items)
      ? testimonialsBlock.items
      : Array.isArray(data.testimonials)
        ? data.testimonials
        : [];
    this.testimonials = testimonialItems.map(t => ({
      name: t.full_name || '',
      position: t.profession || '',
      text: t.testimonial || '',
      rating: t.stars,
      image: t.avatar || ''
    }));

    // "Nuestra Casa" (our_house): map to the gallery shape, ordered by `order`
    this.galleryPhotos = Array.isArray(data.our_house)
      ? data.our_house
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .filter(item => !!item.image)
          .map(item => ({
            image: item.image,
            title: item.title || '',
            description: item.description || ''
          }))
      : [];
  }

  /**
   * Guarantee the cover background video autoplays in loop. Browsers only allow
   * unattended autoplay when the video is muted, and Angular doesn't always
   * reflect the static `muted` attribute, so we set it explicitly and kick off
   * playback once the metadata is ready (swallowing the benign play() rejection).
   */
  playCoverVideo(video: HTMLVideoElement): void {
    video.muted = true;
    video.play().catch(() => { /* autoplay blocked; nothing else to do */ });
  }

  ngOnInit() {
    this.loadLevels();
    this.loadWebPage();

    // blog overlay content
    this.service.getBlogContent().subscribe(
      response => {
        this.gridOverlay = response;
      },
      err => console.log(err),
      () => this.gridOverlay
    );

    // service content
    this.service.getServiceContent().subscribe(
      response => {
        this.serviceItems = response;
      },
      err => console.log(err),
      () => this.serviceItems
    );

    // counter content
    this.service.getCounterContent().subscribe(
      response => {
        this.counterContent = response;
      },
      err => console.log(err),
      () => this.counterContent
    );

    // testimonial content
    this.service.getTestimonialContent().subscribe(
      response => {
        this.testimonialData = response;
      },
      err => console.log(err),
      () => this.testimonialData
    );

    // pricing content
    this.service.getPricingContent().subscribe(
      res => {
        this.pricingContent = res;
      },
      err => console.log(err),
      () => this.pricingContent
    );
  }
}
